// ═══════════════════════════════════════════════════════════════════════════
// drpCoverage.js — what a dairy ALREADY has, per quarter.
//
// The advisor must not put a quarter in front of an agent as though the farm
// were unprotected when we already hold the endorsements. Equally it must not
// claim a farm is covered when the data cannot say — an unresolvable row is
// INCOMPLETE, never zero.
//
// ── THE QUARTER LABEL IS A THIRD FORMAT HERE, AND ONE OF THEM HIDES ITS YEAR ─
//
// `drp_board` uses "2027-Q2" (and historically "Q2-27"). `drp_endorsements`
// uses neither. Measured over all 266 rows:
//
//   "Q4 2026" style (space-separated)          183 rows   year is present
//   "OCTDECY2Q4" style (RMA period code)        64 rows   year is NOT present
//   (19 distinct labels in total)
//
// The second family encodes the month range and a quarter digit but only a
// RELATIVE year token ("Y1"/"Y2"/"Y3"), whose meaning is not documented in
// anything we hold. Guessing it would be inventing a crop year onto real
// coverage, so the year comes from `end_date` instead — which every one of
// those 64 rows carries — and the label is used to CHECK that reading rather
// than to produce it:
//
//   OCTDECY1Q4  end_date 2024-12-31  -> 2024Q4   (Dec in OCTDEC, Q4 == Q4) ok
//   JANMARY2Q1  end_date 2025-03-31  -> 2025Q1   (Mar in JANMAR, Q1 == Q1) ok
//   APRJUNY2Q2  end_date 2025-06-30  -> 2025Q2   (Jun in APRJUN, Q2 == Q2) ok
//   JULSEPY2Q3  end_date 2025-09-30  -> 2025Q3   (Sep in JULSEP, Q3 == Q3) ok
//
// All seven legacy labels reconcile. A row where the end date and the label
// DISAGREE is refused and counted, because the one thing worse than not knowing
// which quarter an endorsement covers is being confidently wrong about it.
//
// ── THE STRUCTURE MISMATCH THAT MAKES THIS A SEPARATE LAYER ────────────────
// The board is priced at 95% coverage and protection factor 1.0. The book buys
// PF 1.45 on average (measured: 266 endorsements, mean 1.45, range to 1.50).
// Protection factor scales the indemnity and the premium, so the board's $/cwt
// net floor is NOT the protection these farms hold. Coverage figures here are
// reported as what the endorsement says; nothing multiplies a board price by a
// farm's PF, because that is a quote, and quoting is the RMA engine's job.
//
// PURE. No fetch.
// ═══════════════════════════════════════════════════════════════════════════

const COVERAGE_STATUS = {
  COVERED: "COVERED",
  PARTIAL: "PARTIAL",
  NONE: "NONE",
  INCOMPLETE: "INCOMPLETE",   // we hold rows we could not resolve — never read as zero
  UNKNOWN: "UNKNOWN",         // no endorsement data supplied at all
};

const MONTH_RANGE = { JANMAR: 3, APRJUN: 6, JULSEP: 9, OCTDEC: 12 };

// Canonical key, matching src/drpDecision.js drpQKey: `${year}Q${q}`.
function endorsementQKey(row) {
  const label = String(row && row.quarter ? row.quarter : "").trim().toUpperCase();

  // "Q4 2026" — year is on the label, use it directly.
  let m = /^Q([1-4])\s+(\d{4})$/.exec(label);
  if (m) return { key: `${m[2]}Q${m[1]}`, basis: "label", conflict: null };

  // "2026-Q4" / "Q4-26" — the board's own spellings, accepted for symmetry.
  m = /^(\d{4})-Q([1-4])$/.exec(label);
  if (m) return { key: `${m[1]}Q${m[2]}`, basis: "label", conflict: null };
  m = /^Q([1-4])-(\d{2})$/.exec(label);
  if (m) return { key: `${2000 + Number(m[2])}Q${m[1]}`, basis: "label", conflict: null };

  // "OCTDECY2Q4" — month range + relative year token + quarter digit. The YEAR
  // is not recoverable from this string, so it comes from end_date and the label
  // is used only to verify.
  m = /^(JANMAR|APRJUN|JULSEP|OCTDEC)Y(\d)Q([1-4])$/.exec(label);
  if (m) {
    const end = row.end_date ? String(row.end_date).slice(0, 10) : null;
    if (!end) return { key: null, basis: null, conflict: `${label} carries no end_date, so its year cannot be resolved` };
    const y = Number(end.slice(0, 4)), mo = Number(end.slice(5, 7));
    const q = Math.floor((mo - 1) / 3) + 1;
    if (MONTH_RANGE[m[1]] !== mo) {
      return { key: null, basis: null, conflict: `${label} says ${m[1]} but end_date ${end} is month ${mo}` };
    }
    if (Number(m[3]) !== q) {
      return { key: null, basis: null, conflict: `${label} says Q${m[3]} but end_date ${end} is Q${q}` };
    }
    return { key: `${y}Q${q}`, basis: "end_date (label verified)", conflict: null };
  }

  return { key: null, basis: null, conflict: label ? `unrecognised quarter label "${row.quarter}"` : "no quarter label" };
}

// Class routing. `class_iii_pct` is an integer percent of the endorsement's
// declared milk assigned to Class III; measured on the book it is 100 on every
// row that carries it, which matches the agency's never-blend philosophy. A NULL
// is NOT read as 0 (that would silently reassign the whole endorsement to Class
// IV) — it is reported as an unstated mix.
function classOf(row) {
  const p = row.class_iii_pct;
  if (p == null) return { cls: null, pct: null, stated: false };
  const n = Number(p);
  if (!Number.isFinite(n)) return { cls: null, pct: null, stated: false };
  if (n >= 100) return { cls: "Class III", pct: 100, stated: true };
  if (n <= 0) return { cls: "Class IV", pct: 0, stated: true };
  return { cls: "BLENDED", pct: n, stated: true };
}

// Roll endorsements up per canonical quarter for ONE farm.
//   endorsements: rows from drp_endorsements (already scoped to the farm)
// Returns a Map keyed by `${year}Q${q}` plus an `unresolved` list.
function coverageByQuarter(endorsements) {
  const out = new Map();
  const unresolved = [];
  for (const row of endorsements || []) {
    const r = endorsementQKey(row);
    if (!r.key) { unresolved.push({ row, reason: r.conflict }); continue; }
    if (!out.has(r.key)) {
      out.set(r.key, {
        qkey: r.key, endorsements: 0, declaredMilkLbs: 0,
        coverageLevels: new Set(), protectionFactors: new Set(), classes: new Set(),
        classMixStated: true, basis: r.basis, liability: 0, producerPremium: 0,
        missingDeclared: 0,
      });
    }
    const g = out.get(r.key);
    g.endorsements += 1;
    if (row.declared_milk_prod == null) g.missingDeclared += 1;
    else g.declaredMilkLbs += Number(row.declared_milk_prod) || 0;
    if (row.coverage_level != null) g.coverageLevels.add(Number(row.coverage_level));
    if (row.protection_factor != null) g.protectionFactors.add(Number(row.protection_factor));
    if (row.liability != null) g.liability += Number(row.liability) || 0;
    if (row.producer_premium != null) g.producerPremium += Number(row.producer_premium) || 0;
    const c = classOf(row);
    if (!c.stated) g.classMixStated = false;
    else g.classes.add(c.cls);
  }
  for (const g of out.values()) {
    g.coverageLevels = Array.from(g.coverageLevels).sort((a, b) => a - b);
    g.protectionFactors = Array.from(g.protectionFactors).sort((a, b) => a - b);
    g.classes = Array.from(g.classes);
  }
  return { byQuarter: out, unresolved };
}

// The status for ONE (quarter, class) cell of the advisor matrix.
//   expectedMilkLbs — the farm's expected production for that quarter, if known.
//                     Absent, we can report that coverage EXISTS but not what
//                     share of production it covers, and we say which.
function coverageForCell({ coverage, qkey, milkClass, expectedMilkLbs = null, hasEndorsementData = true }) {
  if (!hasEndorsementData) {
    return { status: COVERAGE_STATUS.UNKNOWN, detail: "no endorsement data available for this farm", pctOfProduction: null };
  }
  const { byQuarter, unresolved } = coverage;
  const g = byQuarter.get(qkey);
  const unresolvedHere = (unresolved || []).length;

  if (!g) {
    // No resolved endorsement for this quarter. If some rows could not be
    // resolved at all, "none" is not a safe answer.
    if (unresolvedHere) {
      return { status: COVERAGE_STATUS.INCOMPLETE, pctOfProduction: null,
        detail: `no resolved endorsement for this quarter, but ${unresolvedHere} endorsement${unresolvedHere === 1 ? "" : "s"} could not be placed` };
    }
    return { status: COVERAGE_STATUS.NONE, pctOfProduction: null, detail: "no endorsement on file for this quarter" };
  }

  // A cell is only "covered" by endorsements written on the SAME class, unless
  // the mix was never stated — in which case we decline to attribute.
  const classMatches = g.classes.includes(milkClass);
  if (!g.classMixStated) {
    return { status: COVERAGE_STATUS.INCOMPLETE, pctOfProduction: null,
      detail: `${g.endorsements} endorsement${g.endorsements === 1 ? "" : "s"} on this quarter, but the Class III/IV split is not stated on ${g.endorsements === 1 ? "it" : "all of them"}` };
  }
  if (!classMatches) {
    return { status: COVERAGE_STATUS.NONE, pctOfProduction: null,
      detail: `endorsements exist on this quarter but on ${g.classes.join(" / ") || "another class"}, not ${milkClass}` };
  }

  const cov = g.coverageLevels.length === 1 ? g.coverageLevels[0] : null;
  const pf = g.protectionFactors.length === 1 ? g.protectionFactors[0] : null;
  const bits = [`${g.endorsements} endorsement${g.endorsements === 1 ? "" : "s"}`];
  if (g.declaredMilkLbs) bits.push(`${Math.round(g.declaredMilkLbs / 1000).toLocaleString()}k lb declared`);
  if (cov != null) bits.push(`${Math.round(cov * 100)}% coverage`);
  if (pf != null) bits.push(`PF ${pf.toFixed(2)}`);

  if (expectedMilkLbs == null || !(expectedMilkLbs > 0)) {
    return { status: COVERAGE_STATUS.COVERED, pctOfProduction: null,
      detail: bits.join(" · ") + " — share of production unknown (no expected milk on file)",
      declaredMilkLbs: g.declaredMilkLbs, coverageLevel: cov, protectionFactor: pf };
  }
  const pct = g.declaredMilkLbs / expectedMilkLbs;
  return {
    status: pct >= 0.85 ? COVERAGE_STATUS.COVERED : COVERAGE_STATUS.PARTIAL,
    pctOfProduction: pct,
    detail: bits.join(" · ") + ` — ${Math.round(pct * 100)}% of expected production`,
    declaredMilkLbs: g.declaredMilkLbs, coverageLevel: cov, protectionFactor: pf,
  };
}

module.exports = { COVERAGE_STATUS, MONTH_RANGE, endorsementQKey, classOf, coverageByQuarter, coverageForCell };
